import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Check, X, Phone, Mail, Calendar, Users, History, ChevronDown, ChevronUp, Star } from 'lucide-react';

const AdminReservations = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedHistory, setExpandedHistory] = useState({});

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' };
  };

  const fetchReservations = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reservations`, { headers });
      if (!response.ok) throw new Error('Unauthorized');
      const data = await response.json();
      setReservations(data);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les réservations", variant: "destructive" });
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const updateStatus = async (id, newStatus) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/reservations/${id}/status?status=${newStatus}`,
        { method: 'PUT', headers }
      );

      if (response.ok) {
        toast({ title: "Succès", description: `Réservation ${newStatus === 'confirmed' ? 'confirmée' : 'annulée'}` });
        fetchReservations();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de mettre à jour", variant: "destructive" });
    }
  };

  // French date formatting
  const formatDateFrench = (dateStr) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const date = new Date(dateStr + 'T00:00:00');
    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    return `${dayName} ${date.getDate()} ${monthName} ${date.getFullYear()}`;
  };

  const formatTimeFrench = (timeStr) => {
    return timeStr.replace(':', 'h');
  };

  const formatCreatedAt = (dateStr) => {
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const date = new Date(dateStr);
    const monthName = months[date.getMonth()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${date.getDate()} ${monthName} à ${hours}h${minutes}`;
  };

  const filteredReservations = reservations.filter(res => {
    if (filter === 'all') return true;
    return res.status === filter;
  });

  // Group by date
  const groupedReservations = filteredReservations.reduce((acc, res) => {
    const date = res.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(res);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Réservations</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              size="sm"
            >
              Toutes ({reservations.length})
            </Button>
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              onClick={() => setFilter('pending')}
              size="sm"
              className={filter === 'pending' ? 'bg-yellow-500' : ''}
            >
              En attente ({reservations.filter(r => r.status === 'pending').length})
            </Button>
            <Button
              variant={filter === 'confirmed' ? 'default' : 'outline'}
              onClick={() => setFilter('confirmed')}
              size="sm"
              className={filter === 'confirmed' ? 'bg-green-500' : ''}
            >
              Confirmées ({reservations.filter(r => r.status === 'confirmed').length})
            </Button>
            <Button
              variant={filter === 'declined' ? 'default' : 'outline'}
              onClick={() => setFilter('declined')}
              size="sm"
              className={filter === 'declined' ? 'bg-orange-500' : ''}
            >
              Refusées ({reservations.filter(r => r.status === 'declined').length})
            </Button>
            <Button
              variant={filter === 'cancelled' ? 'default' : 'outline'}
              onClick={() => setFilter('cancelled')}
              size="sm"
              className={filter === 'cancelled' ? 'bg-red-500' : ''}
            >
              Annulées ({reservations.filter(r => r.status === 'cancelled').length})
            </Button>
          </div>
        </div>

        {/* Reservations List */}
        {filteredReservations.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">Aucune réservation</p>
          </div>
        ) : (
          Object.entries(groupedReservations)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([date, dayReservations]) => (
              <div key={date} className="mb-8">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar size={20} />
                  {formatDateFrench(date)}
                  <span className="text-sm font-normal text-gray-500">
                    ({dayReservations.length} réservation{dayReservations.length > 1 ? 's' : ''})
                  </span>
                </h2>

                <div className="space-y-4">
                  {dayReservations
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((res) => (
                      <div key={res.id} className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="p-4">
                          <div className="flex flex-wrap justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-2xl font-bold text-gray-900">{formatTimeFrench(res.time)}</span>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  res.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  res.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {res.status === 'confirmed' ? 'Confirmé' :
                                   res.status === 'cancelled' ? 'Annulé' : 'En attente'}
                                </span>
                              </div>

                              <h3 className="text-lg font-semibold text-gray-900">{res.name}</h3>
                              
                              <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Users size={16} />
                                  {res.guests} personne{res.guests > 1 ? 's' : ''}
                                </span>
                                <a href={`tel:${res.phone}`} className="flex items-center gap-1 hover:text-gray-900">
                                  <Phone size={16} />
                                  {res.phone}
                                </a>
                                <a href={`mailto:${res.email}`} className="flex items-center gap-1 hover:text-gray-900">
                                  <Mail size={16} />
                                  {res.email}
                                </a>
                              </div>

                              {res.message && (
                                <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-700">
                                  <strong>Note:</strong> {res.message}
                                </div>
                              )}

                              <p className="text-xs text-gray-400 mt-2">
                                Réservé le {formatCreatedAt(res.created_at)}
                                {res.newsletter && ' • Newsletter acceptée'}
                              </p>

                              {/* Feedback Email Status */}
                              {res.status === 'confirmed' && (
                                <div className="mt-2 flex items-center gap-2 text-xs">
                                  <Star size={14} className={res.feedback_email_sent_at ? 'text-yellow-500' : 'text-gray-300'} />
                                  {res.feedback_email_sent_at ? (
                                    <span className="text-green-600">
                                      Email feedback envoyé le {new Date(res.feedback_email_sent_at).toLocaleDateString('fr-FR', {
                                        day: 'numeric',
                                        month: 'long',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">
                                      Email feedback prévu 24h après la visite
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Email History Section */}
                              {res.email_history && res.email_history.length > 0 && (
                                <div className="mt-3 border-t pt-3">
                                  <button
                                    onClick={() => setExpandedHistory(prev => ({
                                      ...prev,
                                      [res.id]: !prev[res.id]
                                    }))}
                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                                  >
                                    <History size={16} />
                                    Historique des emails ({res.email_history.length})
                                    {expandedHistory[res.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                  </button>
                                  
                                  {expandedHistory[res.id] && (
                                    <div className="mt-2 space-y-2">
                                      {res.email_history.map((log, idx) => (
                                        <div key={idx} className="flex items-start gap-2 text-xs p-2 bg-gray-50 rounded">
                                          <span className={`px-2 py-0.5 rounded text-white ${
                                            log.type === 'confirmation' ? 'bg-green-500' :
                                            log.type === 'decline' ? 'bg-orange-500' :
                                            log.type === 'notification' ? 'bg-blue-500' :
                                            log.type === 'feedback' ? 'bg-yellow-500' : 'bg-gray-500'
                                          }`}>
                                            {log.type === 'confirmation' ? 'Confirmation' :
                                             log.type === 'decline' ? 'Refus' :
                                             log.type === 'notification' ? 'Notification' :
                                             log.type === 'feedback' ? 'Feedback' : log.type}
                                          </span>
                                          <div className="flex-1">
                                            <p className="text-gray-700">{log.recipient}</p>
                                            <p className="text-gray-500">
                                              {new Date(log.sent_at).toLocaleDateString('fr-FR', {
                                                day: 'numeric',
                                                month: 'long',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                              {' • '}
                                              <span className={log.status === 'sent' ? 'text-green-600' : 'text-red-600'}>
                                                {log.status === 'sent' ? 'Envoyé' : 'Échec'}
                                              </span>
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {res.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => updateStatus(res.id, 'confirmed')}
                                  className="bg-green-600 hover:bg-green-700"
                                  size="sm"
                                >
                                  <Check size={18} className="mr-1" />
                                  Confirmer
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => updateStatus(res.id, 'cancelled')}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  size="sm"
                                >
                                  <X size={18} className="mr-1" />
                                  Annuler
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))
        )}
      </main>
    </div>
  );
};

export default AdminReservations;
